import {KeyString} from "@/tools";
import {stringify} from "csv-stringify/browser/esm";

type HeaderGenerator<Fields extends string[]> = {
    [K in Fields[number]]?: (field: K) => string;
};

type FieldMapper<T extends object, Fields extends string[]> = {
    [K in Fields[number]]?: (row: T) => any;
};

type GenerateCSVOptions<T extends object, Field extends KeyString<T>> = {
    /// Header generator function to customize the header names for each field
    headerGenerator?: HeaderGenerator<Field[]>;

    /// If no header generator is provided, this function will be used to generate a fallback header for each field
    fallbackHeaderGenerator?: (field: Field) => string;

    /// Field mapper function to customize the values for each field in the records
    fieldMapper?: FieldMapper<T, Field[]>;
};

function generateCSV<T extends object, Field extends KeyString<T>>(
    records: T[],
    fields: Field[],
    options?: GenerateCSVOptions<T, Field>,
): Promise<string> {
    const {headerGenerator, fallbackHeaderGenerator, fieldMapper} = options ?? {};

    const data: string[] = [];
    let resolver: (s: string) => void = null;
    const promise = new Promise<string>((resolve, _) => {
        resolver = resolve;
    });

    const stringifier = stringify({
        columns: fields as string[],
    });

    stringifier.on("readable", () => {
        while (1) {
            const row = stringifier.read();
            if (!row) break;
            data.push(row);
        }
    });

    stringifier.on("finish", () => {
        resolver(data.join(""));
    });

    stringifier.write(
        fields.map((f) => {
            if (headerGenerator && headerGenerator[f]) {
                return headerGenerator[f](f);
            }
            if (fallbackHeaderGenerator) {
                return fallbackHeaderGenerator(f);
            }
            return f; // Fallback to field name if no header generator is provided
        }),
    );

    for (const record of records) {
        stringifier.write(
            fields.map((f) => {
                const value = record[f];
                if (fieldMapper && fieldMapper[f]) {
                    return fieldMapper[f](record);
                }
                return value; // Fallback to raw value if no field mapper is provided
            }),
        );
    }

    stringifier.end();

    return promise;
}

export async function downloadRecords<T extends object, Field extends KeyString<T>>(
    records: T[],
    fields: Field[],
    filename: string,
    options?: GenerateCSVOptions<T, Field>,
) {
    const data = await generateCSV(records, fields, options);

    const element = document.createElement("a");
    element.setAttribute("href", "data:application/octet-stream," + encodeURIComponent(data));
    element.setAttribute("download", filename + ".csv");
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
}
